import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import Dropzone from 'dropzone';

import { environment } from '../../../../environments/environment';
import { InputComponent } from '../../../shared/input/input.component';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { SvTestPannelService } from '../../../services/sv-test-pannel/sv-test-panel.service';
import { CreatePanelSetupRequestBody } from '../../../interfaces/sv-test-pannel';

@Component({
  selector: 'app-sv-pannel-upload-modal',
  standalone: true,
  imports: [
    ButtonsComponent,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDialogModule,
    InputComponent,
    MatIconModule,
    MatProgressBarModule,
  ],
  templateUrl: './sv-panel-upload-modal.component.html',
  styleUrl: './sv-panel-upload-modal.component.css',
})
export class SvPannelUploadModalComponent {
  inptPlaceHolder: string = $localize`Enter Test Panel Name`;
  testPanelNameControl = new FormControl({ value: '', disabled: false }, []);
  doneBtnLabel: string = $localize`Done`;
  dropzoneInstance!: Dropzone;
  selectFileBtnText: string = $localize`Select File`;
  btnMatStroked: string = 'mat-stroked-button';
  btnStyleStroked: string = 'btn-stroked';
  downloadIcon: string = 'cloud_upload';
  isDoneBtnDisabled: boolean = true;
  fileSelected: boolean = false;
  folderName!: string;
  @ViewChild('dropzoneElement') dropzoneElement!: ElementRef;

  constructor(
    private dialogRef: MatDialogRef<SvPannelUploadModalComponent>,
    private svTestPannelService: SvTestPannelService
  ) {}

  ngAfterViewInit(): void {
    this.initializeDropzone();
  }

  ngOnDestroy(): void {
    if (this.dropzoneInstance) {
      this.dropzoneInstance.destroy(); // Clean up Dropzone instance
    }
  }

  initializeDropzone(): void {
    let that = this;
    const previewTemplate = document.getElementById('preview-template')!.innerHTML;
    this.dropzoneInstance = new Dropzone(this.dropzoneElement.nativeElement, {
      url: `${environment.apiBaseUrl}/api/v1/buckets/upload/panel`,
      method: 'POST',
      paramName: 'file',
      chunking: true,
      chunkSize: 10 * 1024 * 1024, // 10MB chunk size
      parallelUploads: 2,
      maxFilesize: 102400, // Set max file size to 100GB
      acceptedFiles: '.txt, .csv, .zip',
      addRemoveLinks: false,
      clickable: ['.custom-dropzone'],
      previewTemplate: previewTemplate,
      previewsContainer: this.dropzoneElement.nativeElement,
      maxFiles: 1,
      createImageThumbnails: false,
      autoProcessQueue: false,
      retryChunks: true,
      retryChunksLimit: 3,
      init: function () {
        const customDropzoneElement = document.querySelector('.custom-dropzone') as HTMLElement;
        const disableFileSelection = () => {
          customDropzoneElement.classList.add('dropzone-disabled');
        };

        const enableFileSelection = () => {
          customDropzoneElement.classList.remove('dropzone-disabled');
        };
        this.on('addedfile', function (file: any) {
          that.fileSelected = true;
          that.panelSetupApi(file);
          if (that.dropzoneInstance.files.length > 1) {
            // Remove the first file (since only one file is allowed)
            that.dropzoneInstance.removeFile(that.dropzoneInstance.files[0]);
          }
          const thumbnailElement = file.previewElement.querySelector('.dz-thumbnail img');
          thumbnailElement.src = 'assets/icons/zip-file.svg';
          thumbnailElement.alt = 'Custom Thumbnail';
        });

        this.on('uploadprogress', (file: any, progress: number) => {
          const progressBar = file.previewElement.querySelector('[data-dz-uploadprogress]');
          if (progressBar) {
            progressBar.style.width = `${progress}%`;
          }
        });

        this.on('success', function (file) {
          const filenameContainer = file.previewElement.querySelector('.dz-filename-container');
          const completedStatus = file.previewElement.querySelector('.dz-completed-status');
          const progressBar = file.previewElement.querySelector('.dz-progress');
          const infoContainer = file.previewElement.querySelector('.dz-info');
          const fileName = file.previewElement.querySelector('.dz-filename');
          const thumbnailElement = file.previewElement.querySelector('.dz-thumbnail img');
          const thumbnailContainer = file.previewElement.querySelector('.dz-thumbnail');

          if (filenameContainer && completedStatus) {
            (completedStatus as HTMLElement).style.display = 'inline-block';
            (completedStatus as HTMLElement).textContent = $localize`Completed`;
            (completedStatus as HTMLElement).style.color = '#318F9A';
            (completedStatus as HTMLElement).style.marginLeft = '0.75rem';
            (completedStatus as HTMLElement).style.fontSize = '0.75rem';
            (progressBar as HTMLElement).style.display = 'none';
            (infoContainer as HTMLElement).style.marginBottom = '0rem';
            (infoContainer as HTMLElement).style.marginTop = '0.75rem';
            (fileName as HTMLElement).style.color = 'rgba(0, 0, 0, 1)';
            (thumbnailContainer as HTMLElement).style.margin = '0 0.625rem 0.625rem 0';
            (thumbnailElement as HTMLImageElement).src = 'assets/icons/zip-file-bold.svg';
          }
          that.isDoneBtnDisabled = false;
          that.testPanelNameControl.disable();
        });

        this.on('sending', function name(file, xhr, formData) {
          formData.append('folder_name', that.folderName);
          disableFileSelection();
        });

        this.on('error', function (file, errorMessage) {
          enableFileSelection();
          const showError = file.previewElement.querySelector('.dz-error');
          if (errorMessage) {
            (showError as HTMLElement).textContent = $localize`Error while uploading file.`;
          }
        });

        this.on('complete', function (file) {
          disableFileSelection();
        });
      },
    });
    (this.dropzoneInstance as any).componentRef = this;
  }

  onPrimaryAction() {
    this.dialogRef.close(true);
  }

  panelSetupApi(file: Dropzone.DropzoneFile) {
    let data: CreatePanelSetupRequestBody = {
      Name: this.testPanelNameControl.value?.trim() || file.name.split('.').slice(0, -1).join('.'),
    };
    let that = this;
    this.svTestPannelService.panelSetup(data).subscribe({
      next(response: any) {
        that.folderName = response.data.FolderName;
        that.dropzoneInstance.processQueue();
      },
      error(err) {
        that.dropzoneInstance.emit(
          'error',
          that.dropzoneInstance.files[0],
          $localize`Error while creating the setup for file upload.`
        );
        console.error('Error while creating the setup for file upload.', err);
      },
    });
  }
}
